import posthog from "posthog-js";

// Single source of truth for PostHog browser init, shared by two call sites so
// the config can't drift between them:
//   1. instrumentation-client.ts — inits on page load when a current consent
//      record already allows analytics (window.__ENV__.analyticsConsent).
//   2. the consent provider — inits at the moment the user opts in, since on a
//      pre-consent load (1) deliberately did NOT init.
//
// The gate mirrors the original startup guard: a token must be present and this
// must be a real production deploy, so `bun run dev` and tokenless preview
// deploys stay dormant regardless of consent. Idempotent — safe to call twice.
let initialized = false;

// Init PostHog if it isn't already. Returns true if PostHog is now live.
export function initPostHog(): boolean {
	if (initialized) return true;
	const key =
		typeof window !== "undefined" ? window.__ENV__?.posthogKey : undefined;
	if (!key || process.env.NODE_ENV !== "production") return false;
	posthog.init(key, {
		api_host: "/ingest",
		ui_host: "https://us.posthog.com",
		defaults: "2026-01-30",
		capture_exceptions: true,
	});
	initialized = true;
	return true;
}

// Runtime opt-in (user clicks Accept). Inits PostHog if needed — init itself
// starts capturing — then opt_in_capturing() makes the intent explicit and
// clears any prior opt-out flag.
export function optInPostHog(): void {
	if (initPostHog()) posthog.opt_in_capturing();
}

// Runtime opt-out (user clicks Reject / toggles analytics off). Stops capture
// and reset() drops the distinct-id + PostHog's own stored cookies. No-op if
// PostHog was never initialised (the pre-consent default), so nothing to clear.
export function optOutPostHog(): void {
	if (!initialized) return;
	posthog.opt_out_capturing();
	posthog.reset();
}
